import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { fetchReservationChannelCode, fetchReservationMarketSegment, updateReservationChannelCode } from '@/apis/aipass'
import { ChannelCodeType, ChannelType } from '@/models/reservation-channel-code'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { EditFooter } from '@/components/organisms/edit-footer'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { MarketSegmentType } from '@/models/reservation-market-segment'
import { AddFormFieldButton } from '@/components/molecules/add-form-field-button'
import { useSettingsEditPageGuardEffect } from '@/hooks/use-settings-edit-guard'

type ChannelCodeFormType = Partial<ChannelCodeType>
export const SettingReservationChannelCodeEdit: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { errorHandler } = useErrorHandler()
  const [isLoading, setIsLoading] = useState(false)
  const [segmentMasters, setSegmentMasters] = useState<MarketSegmentType[]>([])

  const initialChannelCode: ChannelCodeFormType = {
    id: undefined,
    code: undefined,
    name: undefined,
    type: ChannelType.Custom,
    suggestSegmentId: undefined,
    suggestSegmentName: undefined,
  }

  const {
    control,
    handleSubmit,
    register,
    formState: { isValid },
  } = useForm<{ channels: ChannelCodeFormType[] }>({
    defaultValues: {
      channels: [initialChannelCode],
    },
  })
  const inputChannels = useFieldArray({
    control,
    name: 'channels',
    keyName: 'inputKey',
  })

  const onSaveChannelCodes = async (formValue: { channels: ChannelCodeFormType[] }) => {
    try {
      setIsLoading(true)
      await updateReservationChannelCode(formValue.channels)
      history.push({ pathname: '/setting/reservation/channel-code' })
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  useSettingsEditPageGuardEffect({
    redirectPath: '/setting/reservation/channel-code',
    onAllowed: () => {
      fetchReservationChannelCode().then(inputChannels.replace)
      fetchReservationMarketSegment().then(setSegmentMasters)
    },
  })

  const footerContent = <EditFooter onCancel={history.goBack} onSave={handleSubmit(onSaveChannelCodes)} isDisabledSave={!isValid} />

  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <div css={settingEditContainerStyle}>
        <div className="container-title">
          <p>{t('Channel')}</p>
        </div>
        <div css={settingEditHeaderStyle}>
          <div css={channelCodeWidthStyle}>{t('Channel code')}</div>
          <div css={nameWidthStyle}>{t('Name')}</div>
          <div css={segmentWidthStyle} style={{ marginRight: 30 }}>
            {t('Segment')}
          </div>
        </div>
        <div
          css={settingEditFormStyle}
          style={{
            // 55px(header), 24px(padding), 50px(title), 59px(footer), 32px(th)
            height: window.innerHeight - 55 - 24 * 2 - 50 - 59 - 32,
            overflowY: 'auto',
          }}
        >
          <ul>
            {inputChannels.fields.map((channelCode, index) => (
              <li key={channelCode.inputKey}>
                <div css={channelCodeWidthStyle}>
                  <input
                    {...register(`channels.${index}.code`, { required: true })}
                    type="text"
                    placeholder="0000"
                    css={inputStyle}
                    disabled={!!channelCode.id}
                  />
                </div>
                <div css={nameWidthStyle}>
                  <input
                    {...register(`channels.${index}.name`, { required: true })}
                    type="text"
                    placeholder={t('Channel')}
                    css={inputStyle}
                    disabled={channelCode.type === ChannelType.Default}
                  />
                </div>
                <Controller
                  name={`channels.${index}.suggestSegmentId`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select value={value || ''} onChange={onChange} customStyle={css(segmentWidthStyle, { paddingBottom: 0 })}>
                      <AntSelect.Option value={''}>-</AntSelect.Option>
                      {segmentMasters.map(segmentMaster => (
                        <AntSelect.Option key={`${channelCode.inputKey}-${segmentMaster.id}`} value={segmentMaster.id}>
                          {segmentMaster.name}
                        </AntSelect.Option>
                      ))}
                      {channelCode.suggestSegmentId && !segmentMasters.some(({ id }) => id === channelCode.suggestSegmentId) && (
                        <AntSelect.Option
                          key={`${channelCode.inputKey}-${channelCode.suggestSegmentId}`}
                          value={channelCode.suggestSegmentId}
                        >
                          {channelCode.suggestSegmentName}
                        </AntSelect.Option>
                      )}
                    </Select>
                  )}
                />
                <DeleteIcon
                  style={{ visibility: channelCode.type === ChannelType.Default ? 'hidden' : 'visible' }}
                  onClick={() => inputChannels.remove(index)}
                />
              </li>
            ))}
          </ul>
          <AddFormFieldButton
            onAdd={() => inputChannels.append(initialChannelCode)}
            label={t('Add segment')}
            className="plus-button-block"
          />
        </div>
      </div>
    </SettingsLayout>
  )
}

const settingEditContainerStyle = css({
  height: '100%',
  boxShadow: '0px 0px 6px #0000001A',
  borderRadius: 5,
  background: '#FFF',
  '.container-title': {
    height: 50,
    padding: '16px 32px',
    borderBottom: '1px solid #F2F2F2',
    p: {
      fontSize: 18,
      fontWeight: 'bold',
    },
  },
})

const channelCodeWidthStyle = css({ width: '15%' })
const nameWidthStyle = css({ flex: 1 })
const segmentWidthStyle = css({ width: '30%' })

const settingEditHeaderStyle = css({
  background: '#F5F5F5',
  color: '#676767',
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  padding: '10px 32px',
})

const settingEditFormStyle = css({
  padding: '24px 32px',
  li: {
    display: 'flex',
    padding: '0 0 16px 0',
    '> div': { paddingRight: 16 },
  },
  '.plus-button-block': {
    background: 'none',
    border: 'none',
    justifyContent: 'left',
    marginTop: 0,
    width: 'fit-content',
    ':hover': { background: 'none' },
  },
})

const inputStyle = css({
  width: '100%',
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  padding: '0 16px',
  '&::placeholder': { color: '#CCCCCC' },
})
